<script lang="ts">
  import {
    Info,
    CircleEllipsis,
    Copy,
    Trash2,
    MessageSquareText
  } from "lucide-svelte"
  import { xhr, toast, formatDate, wait } from "$app/utils"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import Dropdown from "$app/components/Dropdown.svelte"
  import { needsUpdate } from "$app/utils/goals.svelte"
  import Modal from "$app/components/Modal.svelte"
  import TaskDetail from "./TaskDetail.svelte"
  import ProgressPanel from "$app/Pages/Plans/Goals/ProgressPanel.svelte"
  import { deleteTask } from "./methods"
  import { appStore } from "$app/store.svelte"

  interface Props {
    task: TaskType
    parentName?: string
  }

  let { task = $bindable(), parentName = "Task" }: Props = $props()

  const last_meeting_date: Date = appStore.last_meeting_date
  const today: Date = appStore.today

  task.needsUpdate = needsUpdate(task, last_meeting_date, today)

  let openProgressDropdown = $state(false)
  let openTaskDropdown = $state(false)
  let openTaskDetail = $state(false)
  let selected = $state(false)

  function updateTask(data): void {
    const idx = appStore.tasks.findIndex((t) => t.id === task.id)
    task.created_at = new Date(data.created_at)
    task.needsUpdate = needsUpdate(task, last_meeting_date, today)
    task.progress_date = new Date(data.created_at)
    appStore.tasks[idx] = task
  }

  function updateTaskStatus(data): void {
    const idx = appStore.tasks.findIndex((t) => t.id === task.id)
    appStore.tasks[idx].status = data.status
  }

  async function duplicateTask(): Promise<void> {
    try {
      const offsetY = window.pageYOffset
      const resp = await xhr.post(
        `/plans/${task.plan_id}/tasks/duplicate.json`,
        { id: task.id }
      )
      appStore.tasks = [...appStore.tasks, resp.data as TaskType]
      await wait(100)
      window.scrollBy(0, offsetY)
    } catch (e) {
      toast("There was an error duplicating the task", "error")
    }
  }
</script>

<div
  class="border-l-4 border-blue-800 shadow-sm select-none"
  data-name="task-detail"
  role="button"
  tabindex="0"
  ondblclick={() => (openTaskDetail = true)}
  data-test="task"
  class:bg-blue-50={selected}
>
  <div class="py-2 border-gray-300 border-b border-t flex items-center">
    <div class="flex w-full ml-6 items-center">
      <div
        class={[
          "text-left whitespace-nowrap text-ellipsis overflow-hidden",
          appStore.page === "assignee" ? "w-7/12" : "w-5/12 lg:w-6/12"
        ]}
      >
        <p
          class="text-ellipsis overflow-hidden text-sm text-gray-800"
          data-name="task-name"
        >
          {task.name}
        </p>

        <p class="hidden lg:inline-block text-xs text-gray-500">{parentName}</p>
      </div>
      <div
        class={[
          "items-center justify-end flex",
          appStore.page === "assignee" ? "w-1/12" : "w-3/12 lg:w-2/12 "
        ]}
      >
        {#if appStore.page !== "assignee"}
          <p
            class="sm:inline-flex text-xs text-gray-800 flex items-center mr-2"
          >
            {task.assignee_name}
          </p>
        {/if}

        {#if appStore.page !== "heirarchy"}
          <Dropdown bind:open={openProgressDropdown}>
            {#snippet button()}
              <span
                class={[
                  "focus:outline-none text-blue-400",
                  task.needsUpdate ? "text-red-500" : ""
                ]}
                title={task.needsUpdate ? "Click to add a new Progress" : ""}
              >
                <MessageSquareText
                  class="w-6 h-6 mr-4 {task.needsUpdate ? 'text-red-400' : ''}"
                />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute -left-48 mt-2 w-96 lg:w-120 bg-white focus:outline-none focus:outline-none z-10"
              >
                <ProgressPanel
                  goal={task}
                  type="task"
                  oncreate={(data) => {
                    openProgressDropdown = false
                    toast("Progress added", "success")
                    updateTask(data)
                  }}
                  onclose={() => (openProgressDropdown = false)}
                />
              </div>
            </div>
          </Dropdown>
        {/if}
      </div>
      <div class="w-4/12 lg:w-2/12 flex items-center justify-center">
        <EditGoalStatus
          goalOrTask={task}
          type="Task"
          editable={true}
          small={true}
          show_border={false}
          onupdate={(data) => updateTask(data)}
        />
      </div>

      <div class="hidden lg:inline-block w-2/12">
        <div class="px-2 flex items-center justify-center">
          {#if task.days_left >= 8 && task.status !== "complete" && task.status !== "not_met"}
            <span
              class="text-xs text-gray-600 bg-indigo-50 px-1 rounded-md py-0.5 whitespace-nowrap"
            >
              {formatDate(task.expected_at, "short")}
            </span>
          {:else if task.days_left > 0 && task.days_left < 8 && task.status !== "complete" && task.status !== "not_met"}
            <span
              class="text-xs text-gray-600 bg-indigo-50 px-1 rounded-md py-0.5"
            >
              {task.days_left} days left
            </span>
          {:else if task.days_left < 0 && task.status !== "complete" && task.status !== "not_met"}
            <span
              class="text-xs text-gray-600 bg-indigo-50 px-1 rounded-md py-0.5"
            >
              Past Due
            </span>
          {/if}
        </div>
      </div>

      <div class="w-1/12 text-center">
        <div class="relative flex items-center justify-center pl-4">
          <Dropdown bind:open={openTaskDropdown}>
            {#snippet button()}
              <span
                data-name="task-context-menu"
                class="rounded-full flex items-center justify-center focus:outline-none"
              >
                <CircleEllipsis class="text-gray-400 size-7" />
              </span>
            {/snippet}

            <div>
              <div
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-core border focus:outline-none z-10"
                role="menu"
              >
                <div class="py-1" role="none">
                  <button
                    onclick={() => {
                      openTaskDropdown = false
                      openTaskDetail = true
                    }}
                    class="btn-dropdown"
                    role="menuitem"
                  >
                    <Info class="text-gray-400 w-4 h-4 mr-3" />
                    See Details</button
                  >
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={() => {
                      openTaskDropdown = false
                      duplicateTask()
                    }}
                  >
                    <Copy class="text-gray-400 w-4 h-4 mr-3" />
                    Duplicate
                  </button>
                  <button
                    class="btn-dropdown"
                    role="menuitem"
                    onclick={async () => {
                      selected = true
                      await wait(40)
                      await deleteTask(task)
                      selected = false
                      openTaskDropdown = false
                    }}
                  >
                    <Trash2 class="text-gray-400 w-4 h-4 mr-3" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  </div>
</div>

<Modal bind:open={openTaskDetail} large={true}>
  <TaskDetail taskId={task.id} />
</Modal>
